import React, { useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import type { TrialLessonDTO } from '../../lib/trials';
import { type MessageDraft, setTrialConsiderUntil } from '../../lib/trials';
import { toast } from 'sonner';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge } from '../ui/wizard-layout';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  standortId?: number;
  onUpdated?: (trial: TrialLessonDTO) => void;
};
type StepState = 1 | 2 | 3;
type StepDelta = -1 | 1;

export default function ConsiderUntilWizardModal({ open, onOpenChange, trial, standortId, onUpdated }: Readonly<Props>) {
  const [step, setStep] = useState<StepState>(1);
  // Der bearbeitete Text aus dem Editor. Bisher ging stattdessen ein fester Satz raus (T-0067).
  const [entwurf, setEntwurf] = useState<MessageDraft | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const moveStep = (delta: StepDelta) => {
    setStep((prev) => {
      const next = (prev + delta) as StepState;
      return Math.min(3, Math.max(1, next)) as StepState;
    });
  };

  const [considerUntil, setConsiderUntil] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });

  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);

  const composerGroups: RecipientGroup[] = useMemo(() => {
    const sp = String((trial as any)?.subprofileId ?? (trial as any)?.subprofile_id ?? '').trim();
    if (!sp) return [];
    let wpUserId = 0;
    let subIndex: number | null = null;
    if (sp.includes('-')) {
      const [a, b] = sp.split('-', 2);
      wpUserId = Number.parseInt(a, 10) || 0;
      subIndex = b ? (Number.parseInt(b, 10) || null) : null;
    } else {
      wpUserId = Number.parseInt(sp, 10) || 0;
    }
    if (!wpUserId) return [];
    return [{
      key: 'student',
      label: 'Teilnehmer',
      recipients: [{ id: `person:${wpUserId}:${subIndex ?? ''}`, type: 'person' as const, wpUserId, subIndex, name: trial.studentName || 'Teilnehmer' }],
      templateKeys: { email: 'trial.considering.student', sms: 'trial.considering.student' },
      vars: { considerUntil },
      defaultSelectedChannels: { email: sendEmail, sms: sendSms },
    }];
  }, [trial, considerUntil, sendEmail, sendSms]);

  const canNext = useMemo(() => {
    if (step === 1) return !!considerUntil;
    return true;
  }, [step, considerUntil]);

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const res = await setTrialConsiderUntil(trial.id, {
        considerUntil,
        standortId,
        channels: { email: sendEmail, sms: sendSms },
        drafts: entwurf ? { student: entwurf } : undefined,
        notify: { student: true, teacher: false },
      });
      const failed = [sendEmail && (res.email as any)?.ok === false ? 'E-Mail' : null, sendSms && (res.sms as any)?.ok === false ? 'SMS' : null].filter(Boolean);
      if (failed.length) toast.warning(`Status gesetzt; Versand fehlgeschlagen: ${failed.join(', ')}`);
      else toast.success('Status „Überlegt noch" wurde gesetzt');
      onUpdated?.(res.trial);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Überlegt bis …"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Kalender' }, { label: 'Nachricht' }, { label: 'Übersicht' }]}
      onBack={step > 1 ? () => moveStep(-1) : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? () => moveStep(1) : undefined}
      canNext={!isSubmitting && step < 3 ? canNext : false}
      onPrimary={step === 3 ? submit : undefined}
      primaryLabel={isSubmitting ? 'Speichere…' : 'Eintragen'}
      canPrimary={step === 3 && !isSubmitting}
      isLoading={isSubmitting}
    >
      <div className="space-y-4 py-4 motion-safe:animate-fade-up">
        {step === 1 && (
          <div className="space-y-2">
            <Label>Überlegt bis</Label>
            <Input type="date" min={new Date().toISOString().slice(0, 10)} value={considerUntil} onChange={(e) => setConsiderUntil(e.target.value)} />
          </div>
        )}

        {step === 2 && composerGroups.length > 0 && standortId > 0 && (
          <CommunicationComposer
            active={open && step === 2}
            standortId={standortId}
            groups={composerGroups}
            onDraftsChange={({ groups: g }) => {
              const s = g['student'];
              if (s) {
                setSendEmail(!!s.selectedChannels.email);
                setSendSms(!!s.selectedChannels.sms);
                setEntwurf({
                  subject: s.email.subjectTpl || '',
                  html: s.email.htmlTpl || '',
                  text: s.email.textTpl || '',
                  sms: s.sms.smsTpl || '',
                });
              }
            }}
          />
        )}
        {step === 2 && composerGroups.length > 0 && standortId <= 0 ? (
          <CommunicationStandortHint />
        ) : null}

        {step === 3 && (
          <WizardCard>
            <WizardSection title="Zusammenfassung">
              <WizardKVGrid>
                <WizardKVItem label="Überlegt bis">{considerUntil}</WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="E-Mail"><WizardYesNoBadge yes={sendEmail} /></WizardKVItem>
                <WizardKVItem label="SMS"><WizardYesNoBadge yes={sendSms} /></WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
          </WizardCard>
        )}
      </div>
    </WizardShell>
  );
}
